import React, { ComponentPropsWithoutRef, useEffect } from "react";

import { MdArrowBack } from "@react-icons/all-files/md/MdArrowBack";
import { MdFilterList } from "@react-icons/all-files/md/MdFilterList";
import { MdSearch } from "@react-icons/all-files/md/MdSearch";
import { UseMutationResult } from "@tanstack/react-query";
import classNames from "classnames";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import { Link, LinkProps } from "react-router-dom-v5-compat";
import { twMerge } from "tailwind-merge";

import { getSubmissionStateFromMutation } from "Atoms/Buttons/SubmitButton";
import Icon from "Atoms/Icons";

import type { Dietary as DietaryType } from "Helpers/DataDefinitions/Dietaries";
import type { ResourceState } from "Helpers/DataDefinitions/ResourceStates";

import { useBrand } from "../BrandContext";
import { LoadingSpinner } from "../Loading";
import { useRecordFeatureUsage } from "../queries";
import { PriceUnit } from "../schema";

export const TitleBar = ({
  children,
  alwaysShowBackButton,
  backTo,
  hideBackButton,
}: {
  children: React.ReactNode;
  alwaysShowBackButton?: boolean;
  backTo?: LinkProps["to"];
  hideBackButton?: boolean;
}) => {
  return (
    <div className="sticky inset-x-0 top-0 z-40 -mx-16 flex items-center justify-between bg-venue-secondary px-8 py-8 text-2xl font-medium md:static md:mx-0">
      {!hideBackButton && (
        <div
          className={classNames("w-32", {
            "md:hidden": !alwaysShowBackButton,
          })}
        >
          <Link to={backTo || ".."} className="text-xl">
            <MdArrowBack />
          </Link>
        </div>
      )}
      <div className="flex-1 text-center">{children}</div>
      {!hideBackButton && (
        <div
          className={classNames("w-32", {
            "md:hidden": !alwaysShowBackButton,
          })}
        ></div>
      )}
    </div>
  );
};

export const ResponsiveTitleBar = ({
  children,
  backTo,
}: {
  children: React.ReactNode;
  backTo?: LinkProps["to"];
}) => {
  return (
    <div>
      <div className="sticky top-0 z-10 md:hidden">
        <TitleBar backTo={backTo}>
          <div className="md:text-4xl md:font-bold">{children}</div>
        </TitleBar>
      </div>
      <div className="hidden md:block">
        <TitleBar backTo={backTo}>
          <div className="md:text-4xl md:font-bold">{children}</div>
        </TitleBar>
      </div>
    </div>
  );
};

interface SearchFieldProps {
  searchTerm?: string;
  onChange?: (searchTerm: string) => void;
}

export const ErrorBox = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="rounded-venue-brand border-2 border-venue-highlight bg-venue-tertiary p-12 text-center text-venue-highlight shadow-venue-brand-style">
      {children}
    </div>
  );
};
export const PageContent = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={`p-16 ${className} `}>{children}</div>;
};
export const SearchField = ({ searchTerm, onChange }: SearchFieldProps) => {
  return (
    <div className="relative flex items-center rounded-venue-brand bg-venue-tertiary p-8">
      <MdSearch className="flex-none text-xl" />
      <input
        type="text"
        placeholder="Search"
        className="w-full bg-venue-tertiary px-8 outline-hidden"
        value={searchTerm}
        onChange={(e) => onChange && onChange(e.target.value)}
      />
      <MdFilterList className="flex-none text-xl" />
    </div>
  );
};

export {
  useSubmitButton,
  getSubmissionStateFromMutation,
} from "Atoms/Buttons/SubmitButton";

interface ButtonOptions {
  primary?: boolean;
  secondary?: boolean;
  alert?: boolean;
  children: React.ReactNode;
}
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonOptions & {
    tag?: "button";
    mutation?: UseMutationResult;
    submissionState?: ResourceState;
    submissionOptions?: {
      loadingMessage?: string;
      errorMessage?: string;
    };
  };
type LinkButtonProps = ButtonOptions & { to: string };

export const Button = ({
  primary,
  secondary,
  alert,
  children,
  submissionState,
  submissionOptions,
  mutation,
  className,
  ...props
}: ButtonProps) => {
  const Tag = props.tag || "button";
  const submissionStateFromMutation = mutation
    ? getSubmissionStateFromMutation(mutation)
    : undefined;
  const finalSubmissionState =
    submissionState || submissionStateFromMutation || "LOADED";

  return (
    <Tag
      disabled={finalSubmissionState === "LOADING" || props.disabled}
      className={twMerge(
        "block w-full cursor-pointer items-center justify-center rounded-venue-brand p-12 text-center text-lg font-bold disabled:cursor-not-allowed disabled:opacity-80",
        primary && "bg-venue-primary text-venue-secondary",
        secondary && "border-2 border-venue-primary bg-venue-tertiary",
        alert && "bg-venue-highlight text-venue-secondary",
        className,
      )}
      {...props}
    >
      <ButtonContent
        submissionState={finalSubmissionState}
        submissionOptions={submissionOptions}
      >
        {children}
      </ButtonContent>
    </Tag>
  );
};

const priceUnitsPerSymbols = {
  item: "/item",
  kilogram: "/Kg",
  "100 gram": "/100g",
  gram: "/g",
  litre: "/L",
  millilitre: "/mL",
  open: "",
};

const countDecimals = (value: number, maxDecimals = 2): number => {
  if (!Number.isFinite(value)) return 0;
  if (Math.floor(value) === value) return 0;

  // Round to a reasonable number of decimals first
  const rounded = Number(value.toFixed(maxDecimals));

  // Use toFixed again to avoid scientific notation, then trim trailing zeros
  const [, decimalPart = ""] = rounded.toFixed(maxDecimals).split(".");
  // Remove trailing zeros of the decimal part
  const trimmed = decimalPart.replace(/0+$/, "");

  return trimmed.length;
};

export const Price = ({
  value,
  priceUnit,
  showCurrency,
  showDecimals,
  fromCents = false,
}: {
  value: number | string;
  priceUnit?: PriceUnit;
  showCurrency?: boolean;
  showDecimals?: boolean;
  fromCents?: boolean;
}) => {
  const parsedNumber = typeof value == "string" ? parseFloat(value) : value;
  const safeNumber = isNaN(parsedNumber) ? 0 : parsedNumber;
  const { currencyDisplayType } = useBrand();
  const { t } = useTranslation("onsite");

  const hasSymbol =
    showCurrency ||
    currencyDisplayType == "terse_with_symbol" ||
    currencyDisplayType == "normal_with_symbol";
  const isTerse =
    !showDecimals &&
    (currencyDisplayType == "terse_with_symbol" ||
      currencyDisplayType == "terse");

  const displayNumber = fromCents ? safeNumber / 100 : safeNumber;
  const currencyOptions: currency.Options = {
    symbol: hasSymbol ? t("currencySymbol") : "",
    precision: isTerse ? countDecimals(displayNumber) : 2,
  };
  const hasPriceUnit = priceUnit && priceUnit != "item";
  const price = currency(displayNumber, currencyOptions).format();
  return (
    <>
      {price}
      {hasPriceUnit && priceUnitsPerSymbols[priceUnit]}
    </>
  );
};

export const PriceLine = ({
  value,
  label,
  secondary,
}: {
  value: React.ReactNode;
  label: React.ReactNode;
  secondary?: boolean;
}) => {
  return (
    <div className="flex justify-end">
      <h3
        className={twMerge(
          "flex-1 text-2xl font-bold",
          secondary && "text-xl font-normal",
        )}
      >
        {label}{" "}
      </h3>
      <div className="text-2xl font-extrabold">{value}</div>
    </div>
  );
};
const ButtonContent = ({
  children,
  submissionState,
  submissionOptions,
}: {
  submissionState: ResourceState;
  submissionOptions?: {
    loadingMessage?: string;
    errorMessage?: string;
  };
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation("onsite");

  switch (submissionState) {
    case "LOADED":
      return <>{children}</>;
    case "LOADING":
    case "UNLOADED":
      return (
        <>
          {submissionOptions?.loadingMessage || t("buttons.saving")}{" "}
          <LoadingSpinner
            icon="loading"
            color={undefined}
            size="xs"
            className="inline-block animate-spin align-middle"
          />
        </>
      );
    case "ERROR":
    case "DIRTY":
      return (
        <>
          {submissionOptions?.errorMessage || (
            <>
              {t("buttons.savingFailed")}{" "}
              <Icon
                className={undefined}
                color={undefined}
                size="base"
                icon="error"
              />
            </>
          )}
        </>
      );
  }
};
export const LinkButton = ({
  to,
  primary,
  secondary,
  children,
  ...props
}: LinkButtonProps) => {
  return (
    <Link
      to={to}
      className={classNames(
        "mx-auto block w-full max-w-screen-sm cursor-pointer items-center justify-center rounded-venue-brand p-12 text-center text-lg font-bold",
        {
          "bg-venue-primary text-venue-secondary": primary,
          "border-2 border-venue-primary": secondary,
        },
      )}
      {...props}
    >
      {children}
    </Link>
  );
};

export const ResponsivePrimaryAction = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <>
      <div className="hidden flex-1 md:block">{children}</div>
      <PrimaryAction>{children}</PrimaryAction>
    </>
  );
};
export const PrimaryAction = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="fixed inset-x-0 bottom-0 bg-venue-secondary px-16 py-8 md:hidden">
      {children}
    </div>
  );
};

export const Dietary = ({ dietary }: { dietary: DietaryType }) => {
  const Icon = dietary.Icon;
  const { t } = useTranslation("onsite");
  return (
    <div className="space-x-8 flex items-center rounded-full bg-venue-tertiary px-8 py-4">
      <Icon />
      <div className="text-lg font-medium text-venue-primary">
        {t(`dietaries.${dietary.value}`)}
      </div>
    </div>
  );
};

export const DevNote = ({ note }: { note: string }) => {
  return <div className="text-xs italic"> 👨‍💻 - {note} </div>;
};
export const RecordFeatureView = ({
  featureName,
  disableRecord,
  children,
}: {
  featureName: string;
  disableRecord?: boolean;
  children: React.ReactNode;
}) => {
  const recordFeatureUsage = useRecordFeatureUsage();

  useEffect(() => {
    if (!disableRecord) {
      recordFeatureUsage.mutate({ featureName, usageType: "view" });
    }
  }, [featureName, disableRecord, recordFeatureUsage.mutate]);

  return <>{children}</>;
};
export interface SelectOption {
  value: string | number | undefined;
  label: string | null;
}

interface SelectInputProps {
  children?: React.ReactNode;
  options?: SelectOption[];
}
export const SelectInput = React.forwardRef<
  HTMLSelectElement,
  SelectInputProps & ComponentPropsWithoutRef<"select">
>(({ options, children, ...props }, ref) => {
  return (
    <div className="relative">
      <select
        className="block w-full appearance-none rounded-venue-brand bg-venue-tertiary p-12 pr-32 shadow-venue-brand-style disabled:cursor-not-allowed"
        {...props}
        ref={ref}
      >
        {children ||
          (options || []).map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-8 text-venue-primary">
        <Icon size="xl" key="expandMore" icon="expandMore" color="current" />
      </div>
    </div>
  );
});

SelectInput.displayName = "SelectInput";
